import { useEffect, useState, useRef, useCallback, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { loadMicrofrontend, loadRemoteModule } from "@/utils/microfrontendLoader";
import { getMicrofrontendByRoute } from "@/config/microfrontends";
import { useShellAuth } from "@/stores/selector";
import { getAccessToken } from "@/stores/getAccessToken";
import type { ShellAuthMessage } from "@/types/shellAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RemoteCrashed } from "@/components/RemoteCrashed";
import { useUserApplications } from "@/hooks/useUserApplications";

interface MicrofrontendLoaderProps {
  route: string;
  bundleUrl?: string;
  appName?: string;
}

export function MicrofrontendLoader({
  route,
  bundleUrl,
  appName,
}: MicrofrontendLoaderProps) {
  const [Component, setComponent] = useState<React.ComponentType<any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { applications } = useUserApplications();
  const location = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const shellAuth = useShellAuth();
  console.log("route")
  const config = getMicrofrontendByRoute(route, applications);
  console.log("config",config)
  const effectiveBundleUrl = bundleUrl || config?.bundleUrl || "";
  const effectiveName = appName || config?.name || route;
  const effectiveType = config?.type || (effectiveBundleUrl ? "iframe" : undefined);

  const getTargetOrigin = useCallback((): string | null => {
    if (!effectiveBundleUrl) return null;
    try {
      return new URL(effectiveBundleUrl).origin;
    } catch {
      return null;
    }
  }, [effectiveBundleUrl]);

  /**
   * Send auth to iframe
   */
  const sendAuthToIframe = useCallback(() => {
    const iframe = iframeRef.current;
    const targetOrigin = getTargetOrigin();
    if (!iframe?.contentWindow || !targetOrigin) return;

    const message: ShellAuthMessage = {
      type: "SHELL_AUTH",
      payload: {
        user: shellAuth.user,
        isAuthenticated: shellAuth.isAuthenticated,
        permissions: shellAuth.permissions,
        accessToken: null,
        appName: effectiveName,
      },
    };

    console.log("SHELL: Sending SHELL_AUTH", message);
    iframe.contentWindow.postMessage(message, targetOrigin);
  }, [
    shellAuth.user,
    shellAuth.isAuthenticated,
    shellAuth.permissions,
    effectiveName,
    getTargetOrigin,
  ]);

  /**
   * Listen for iframe messages (Handshake + Token)
   */
  useEffect(() => {
    if (effectiveType !== "iframe") return;

    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;

      if (event.data?.type === "MFE_READY") {
        console.log("SHELL: MFE_READY received");
        sendAuthToIframe();
      }

      if (event.data?.type === "REQUEST_ACCESS_TOKEN") {
        const accessToken = await getAccessToken();
        iframeRef.current?.contentWindow?.postMessage(
          { type: "SHELL_ACCESS_TOKEN", payload: { accessToken } },
          targetOrigin
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [effectiveType, getTargetOrigin, sendAuthToIframe]);

  /**
   * IFRAME MODE
   */
  if (effectiveType === "iframe") {
    return (
      <div className="w-full h-[calc(100vh)]">
        <iframe
          ref={iframeRef}
          src={effectiveBundleUrl}
          title={effectiveName}
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-scripts allow-same-origin allow-downloads"
          allow="clipboard-read; clipboard-write"

        />
      </div>
    );
  }

  /**
   * FEDERATION MODE (unchanged)
   */
  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        if (!config) {
          throw new Error(`No microfrontend configuration found for route: ${route}`);
        }

        let component: React.ComponentType<any>;

        if (config.type === "federation" && config.remoteName && config.exposedModule) {
          component = await loadRemoteModule(
            config.bundleUrl,
            config.remoteName,
            config.exposedModule
          );
        } else {
          component = (await loadMicrofrontend(config.bundleUrl)).component;
        }

        if (mounted) {
          setComponent(() => component);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to load microfrontend:", err);
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load microfrontend");
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [route, location.pathname, config]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!Component) return null;

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ErrorBoundary fallback={<RemoteCrashed />}>
        <Component
          user={shellAuth.user}
          isAuthenticated={shellAuth.isAuthenticated}
          permissions={shellAuth.permissions}
          getAccessToken={getAccessToken}
          appName={effectiveName}
        />
      </ErrorBoundary>
    </Suspense>
  );
}
